import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Droplets, BarChart3, CircleDollarSign, ExternalLink, Loader2 } from "lucide-react";

const MINT = process.env.REACT_APP_JETDOG_MINT;

function fmtUsd(n) {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function fmtPrice(p) {
  if (p == null) return "—";
  const v = Number(p);
  if (v >= 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(8)}`;
}

const TFS = [
  { key: "24H", tf: "hour", limit: 24 },
  { key: "7D", tf: "hour", limit: 168 },
  { key: "30D", tf: "day", limit: 30 },
];

const PriceChart = () => {
  const [tfKey, setTfKey] = useState("7D");
  const [candles, setCandles] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const { tf, limit } = TFS.find((t) => t.key === tfKey);
    setCandles(null);
    setFailed(false);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/price/ohlcv?tf=${tf}&limit=${limit}`)
      .then((r) => r.json())
      .then((d) => alive && setCandles(d.candles || []))
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [tfKey]);

  const W = 800;
  const H = 280;
  const PAD = 8;

  let path = "";
  let area = "";
  let last = null;
  let lastXY = null;
  let min = 0;
  let max = 0;
  let changePct = 0;
  if (candles && candles.length > 1) {
    const closes = candles.map((c) => c.c);
    min = Math.min(...closes);
    max = Math.max(...closes);
    const span = max - min || max || 1;
    const pts = closes.map((v, i) => [
      PAD + (i / (closes.length - 1)) * (W - PAD * 2),
      H - PAD - ((v - min) / span) * (H - PAD * 2),
    ]);
    path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    area = `${path} L${pts[pts.length - 1][0].toFixed(1)},${H} L${pts[0][0].toFixed(1)},${H} Z`;
    last = closes[closes.length - 1];
    lastXY = pts[pts.length - 1];
    changePct = ((last - closes[0]) / closes[0]) * 100;
  }
  const up = changePct >= 0;
  const lineColor = up ? "#00E5FF" : "#FF007F";

  return (
    <div className="gg-panel gg-glow-cyan rounded-2xl overflow-hidden" data-testid="price-chart-embed">
      <div className="flex items-center justify-between px-4 pt-3 flex-wrap gap-2">
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--jd-ocean)]">
          $JETDOG / USD
          {candles && candles.length > 1 && (
            <span className={`ml-3 font-bold ${up ? "text-[var(--jd-green)]" : "text-[var(--jd-accent)]"}`} data-testid="chart-change">
              {up ? "▲" : "▼"} {Math.abs(changePct).toFixed(1)}% ({tfKey})
            </span>
          )}
        </p>
        <div className="inline-flex rounded-lg overflow-hidden border border-white/10">
          {TFS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTfKey(t.key)}
              data-testid={`chart-tf-${t.key}`}
              className={`px-3 py-1 font-mono font-bold text-xs transition-colors ${
                tfKey === t.key ? "bg-[var(--jd-primary)] text-black" : "text-[var(--jd-ocean)] hover:text-[var(--jd-text)]"
              }`}
            >
              {t.key}
            </button>
          ))}
        </div>
      </div>

      {failed ? (
        <div className="h-64 flex items-center justify-center text-[var(--jd-ocean)] text-sm" data-testid="chart-error">
          Chart data unavailable right now — try again shortly.
        </div>
      ) : !candles ? (
        <div className="h-64 flex items-center justify-center" data-testid="chart-loading">
          <Loader2 className="animate-spin text-[var(--jd-primary)]" size={32} />
        </div>
      ) : candles.length < 2 ? (
        <div className="h-64 flex items-center justify-center text-[var(--jd-ocean)] text-sm">Not enough data yet.</div>
      ) : (
        <div className="relative px-2 pb-2">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full block" style={{ height: "clamp(260px, 42vh, 420px)" }}>
            <defs>
              <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lineColor} stopOpacity="0.30" />
                <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
              </linearGradient>
              <filter id="chartGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="4" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {[0.25, 0.5, 0.75].map((g) => (
              <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            ))}
            <path d={area} fill="url(#chartFill)" />
            <path d={path} fill="none" stroke={lineColor} strokeWidth="2.5" filter="url(#chartGlow)" vectorEffect="non-scaling-stroke" />
            {lastXY && (
              <circle cx={lastXY[0]} cy={lastXY[1]} r="4" fill={lineColor} filter="url(#chartGlow)">
                <animate attributeName="r" values="3;6;3" dur="1.6s" repeatCount="indefinite" />
              </circle>
            )}
          </svg>
          <div className="absolute top-2 left-4 font-mono text-[10px] text-[var(--jd-ocean)]">High {fmtPrice(max)}</div>
          <div className="absolute bottom-3 left-4 font-mono text-[10px] text-[var(--jd-ocean)]">Low {fmtPrice(min)}</div>
          <div className="absolute top-2 right-4 font-mono text-xs font-bold" style={{ color: lineColor, textShadow: `0 0 10px ${lineColor}` }} data-testid="chart-last-price">
            {fmtPrice(last)}
          </div>
        </div>
      )}
    </div>
  );
};

export default function LivePrice() {
  const [pair, setPair] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${MINT}`);
        const data = await res.json();
        const best = (data.pairs || []).sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];
        if (alive && best) setPair(best);
      } catch (e) {}
      if (alive) setLoading(false);
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const change = Number(pair?.priceChange?.h24 ?? 0);
  const up = change >= 0;

  const stats = [
    { icon: CircleDollarSign, label: "Market Cap", value: fmtUsd(pair?.marketCap ?? pair?.fdv) },
    { icon: Droplets, label: "Liquidity", value: fmtUsd(pair?.liquidity?.usd) },
    { icon: BarChart3, label: "24h Volume", value: fmtUsd(pair?.volume?.h24) },
  ];

  return (
    <section id="price" className="section relative bg-[#07070E]" data-testid="live-price-section">
      <div
        className="absolute top-10 right-10 w-[420px] h-[300px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(0,229,255,0.08), transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="max-w-6xl mx-auto px-4 relative">
        <div className="text-left md:text-center mb-10">
          <p className="font-accent text-2xl text-[var(--jd-secondary)]">straight from the ocean charts</p>
          <h2 className="font-display font-bold text-4xl md:text-6xl uppercase mt-2 neon-title-gold">
            Live Price
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-10" data-testid="live-price-loading">
            <Loader2 className="animate-spin text-[var(--jd-primary)]" size={40} />
          </div>
        ) : !pair ? (
          <div className="gg-panel rounded-2xl p-8 text-center max-w-xl mx-auto" data-testid="live-price-empty">
            <p className="font-display font-bold text-2xl uppercase">Chart data warming up…</p>
            <p className="mt-2 text-sm text-[var(--jd-ocean)]">DexScreener has not indexed the pool yet. Check back soon.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="gg-panel gg-glow-gold rounded-2xl p-5 card-lift" data-testid="price-card">
                <p className="text-[10px] uppercase tracking-widest font-mono text-[var(--jd-ocean)]">$JETDOG Price</p>
                <p className="font-mono font-bold text-2xl md:text-3xl mt-1 text-[var(--jd-secondary)]" data-testid="price-value">{fmtPrice(pair.priceUsd)}</p>
                <div
                  className={`inline-flex items-center gap-1 mt-2 rounded-full px-3 py-0.5 text-xs font-mono font-bold border ${up ? "bg-[rgba(57,255,20,0.12)] text-[var(--jd-green)] border-[rgba(57,255,20,0.4)]" : "bg-[rgba(255,0,63,0.12)] text-red-400 border-red-500/40"}`}
                  data-testid="price-change-24h"
                >
                  {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {change.toFixed(2)}% (24h)
                </div>
              </div>
              {stats.map((st) => (
                <div key={st.label} className="gg-panel rounded-2xl p-5 card-lift" data-testid={`price-stat-${st.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <st.icon size={22} className="text-[var(--jd-primary)]" />
                  <p className="text-[10px] uppercase tracking-widest font-mono text-[var(--jd-ocean)] mt-2">{st.label}</p>
                  <p className="font-mono font-bold text-2xl md:text-3xl mt-1">{st.value}</p>
                </div>
              ))}
            </div>

            <PriceChart />

            <div className="text-center mt-6">
              <a
                href={`https://dexscreener.com/solana/${pair.pairAddress}`}
                target="_blank"
                rel="noreferrer"
                className="nb-btn nb-btn--white inline-flex items-center gap-2"
                data-testid="dexscreener-link"
              >
                Open on DexScreener <ExternalLink size={16} />
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
